"use client";

import { cn } from "@resume-studio/utils";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";

import { Button } from "./button";
import { Checkbox } from "./checkbox";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./drawer";
import { Label } from "./label";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";

// ============================================================================
// Date Parsing Utilities (with backward compatibility)
// ============================================================================

const ISO_DATE_PATTERN = /^\d{4}-\d{2}$/;
const ISO_RANGE_PATTERN = /^\d{4}-\d{2}\|(\d{4}-\d{2}|current)$/;

type ParsedDate = {
  year: number;
  month: number;
};

type ParsedDateValue = {
  isLegacy: boolean;
  startDate?: ParsedDate;
  endDate?: ParsedDate | "current";
  legacyText?: string;
};

/**
 * Parse a date value string (handles both ISO and legacy formats)
 */
export function parseDateValue(value: string): ParsedDateValue {
  if (!value || value.trim() === "") {
    return { isLegacy: false };
  }

  const trimmed = value.trim();

  // Check for ISO single date: "2023-03"
  if (ISO_DATE_PATTERN.test(trimmed)) {
    const [year, month] = trimmed.split("-").map(Number);
    return {
      isLegacy: false,
      startDate: { year, month },
    };
  }

  // Check for ISO range: "2023-03|2024-01" or "2023-03|current"
  if (ISO_RANGE_PATTERN.test(trimmed)) {
    const [startStr, endStr] = trimmed.split("|");
    const [startYear, startMonth] = startStr.split("-").map(Number);
    const startDate: ParsedDate = { year: startYear, month: startMonth };

    if (endStr === "current") {
      return {
        isLegacy: false,
        startDate,
        endDate: "current",
      };
    }

    const [endYear, endMonth] = endStr.split("-").map(Number);
    return {
      isLegacy: false,
      startDate,
      endDate: { year: endYear, month: endMonth },
    };
  }

  // Legacy format - return as-is
  return {
    isLegacy: true,
    legacyText: trimmed,
  };
}

/**
 * Format a ParsedDate to ISO string (YYYY-MM)
 */
function formatToISO(date: ParsedDate): string {
  return `${date.year}-${String(date.month).padStart(2, "0")}`;
}

/**
 * Build the value string from parsed components
 */
function buildValueString(
  startDate: ParsedDate | undefined,
  endDate: ParsedDate | "current" | undefined,
  mode: "single" | "range",
): string {
  if (!startDate) return "";

  const startStr = formatToISO(startDate);

  if (mode === "single") {
    return startStr;
  }

  if (!endDate) {
    return startStr;
  }

  if (endDate === "current") {
    return `${startStr}|current`;
  }

  return `${startStr}|${formatToISO(endDate)}`;
}

/**
 * Format date for display (user-friendly)
 * Default format: MM/YYYY (e.g., 12/2020, 12/2020 - 10/2021, 12/2020 - Current)
 */
export function formatDateForDisplay(value: string): string {
  const parsed = parseDateValue(value);

  if (parsed.isLegacy && parsed.legacyText) {
    return parsed.legacyText;
  }

  if (!parsed.startDate) {
    return "";
  }

  const formatDate = (date: ParsedDate) => {
    return `${String(date.month).padStart(2, "0")}/${date.year}`;
  };

  const startFormatted = formatDate(parsed.startDate);

  if (!parsed.endDate) {
    return startFormatted;
  }

  if (parsed.endDate === "current") {
    return `${startFormatted} - Current`;
  }

  return `${startFormatted} - ${formatDate(parsed.endDate)}`;
}

// ============================================================================
// useMediaQuery Hook
// ============================================================================

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const media = window.matchMedia(query);
    setMatches(media.matches);

    const listener = (event: MediaQueryListEvent) => setMatches(event.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
}

// ============================================================================
// Month/Year Picker Sub-component
// ============================================================================

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

type MonthYearPickerProps = {
  value?: ParsedDate;
  onChange: (date: ParsedDate) => void;
  label?: string;
  disabled?: boolean;
  maxDate?: ParsedDate;
  minDate?: ParsedDate;
};

const MonthYearPicker = ({
  value,
  onChange,
  label,
  disabled,
  maxDate,
  minDate,
}: MonthYearPickerProps) => {
  const currentYear = new Date().getFullYear();
  const [viewYear, setViewYear] = React.useState(value?.year ?? currentYear);

  // Generate year range (20 years back to 10 years forward)
  const years = React.useMemo(() => {
    const result: number[] = [];
    for (let y = currentYear + 10; y >= currentYear - 50; y--) {
      result.push(y);
    }
    return result;
  }, [currentYear]);

  const isMonthDisabled = (month: number) => {
    if (disabled) return true;
    if (minDate) {
      if (viewYear < minDate.year || (viewYear === minDate.year && month < minDate.month)) {
        return true;
      }
    }
    if (maxDate) {
      if (viewYear > maxDate.year || (viewYear === maxDate.year && month > maxDate.month)) {
        return true;
      }
    }
    return false;
  };

  return (
    <div className={cn("space-y-3", disabled && "opacity-50")}>
      {label && <Label className="block text-xs font-medium text-muted-foreground">{label}</Label>}

      {/* Year Navigation */}
      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          disabled={disabled}
          onClick={() => setViewYear((y) => y - 1)}
        >
          <ChevronLeft className="size-4" />
        </Button>

        <Select
          value={String(viewYear)}
          onValueChange={(v) => setViewYear(Number(v))}
          disabled={disabled}
        >
          <SelectTrigger className="h-8 min-w-[80px] flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          disabled={disabled}
          onClick={() => setViewYear((y) => y + 1)}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>

      {/* Month Grid */}
      <div className="grid grid-cols-4 gap-1">
        {MONTHS.map((monthName, index) => {
          const month = index + 1;
          const isSelected = value?.year === viewYear && value?.month === month;
          const isDisabled = isMonthDisabled(month);

          return (
            <Button
              key={monthName}
              type="button"
              variant={isSelected ? "primary" : "ghost"}
              size="sm"
              disabled={isDisabled}
              className={cn("h-8 text-xs", isSelected && "bg-primary text-primary-foreground")}
              onClick={() => {
                if (!isDisabled) {
                  onChange({ year: viewYear, month });
                }
              }}
            >
              {monthName}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// Shared Picker Content Component
// ============================================================================

type PickerContentProps = {
  mode: "single" | "range";
  legacyMode: boolean;
  legacyText: string;
  startDate?: ParsedDate;
  endDate?: ParsedDate | "current";
  isCurrent: boolean;
  onStartDateChange: (date: ParsedDate) => void;
  onEndDateChange: (date: ParsedDate) => void;
  onCurrentToggle: (checked: boolean) => void;
  onLegacyTextChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSwitchToPickerMode: () => void;
  /** Use vertical stacked layout for range mode (for mobile) */
  stacked?: boolean;
  /** Label for the "current" checkbox (default: "I currently work here") */
  presentLabel?: string;
};

const PickerContent = ({
  mode,
  legacyMode,
  legacyText,
  startDate,
  endDate,
  isCurrent,
  onStartDateChange,
  onEndDateChange,
  onCurrentToggle,
  onLegacyTextChange,
  onSwitchToPickerMode,
  stacked = false,
  presentLabel = "I currently work here",
}: PickerContentProps) => {
  if (legacyMode) {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Legacy date format detected</Label>
          <input
            type="text"
            value={legacyText}
            onChange={onLegacyTextChange}
            className="flex h-9 w-full rounded-lg border border-input bg-secondary px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            placeholder="e.g., March 2023 - Present"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={onSwitchToPickerMode}
        >
          Switch to date picker
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {mode === "range" ? (
        stacked ? (
          // Mobile: Stacked vertical layout
          <div className="flex flex-col gap-4">
            <MonthYearPicker
              label="Start Date"
              value={startDate}
              onChange={onStartDateChange}
              maxDate={endDate && endDate !== "current" ? endDate : undefined}
            />
            <div className="h-px bg-border" />
            <MonthYearPicker
              label="End Date"
              value={endDate && endDate !== "current" ? endDate : undefined}
              onChange={onEndDateChange}
              minDate={startDate}
              disabled={isCurrent}
            />
          </div>
        ) : (
          // Desktop: Side-by-side layout
          <div className="flex gap-6">
            <MonthYearPicker
              label="Start Date"
              value={startDate}
              onChange={onStartDateChange}
              maxDate={endDate && endDate !== "current" ? endDate : undefined}
            />
            <div className="w-px bg-border" />
            <MonthYearPicker
              label="End Date"
              value={endDate && endDate !== "current" ? endDate : undefined}
              onChange={onEndDateChange}
              minDate={startDate}
              disabled={isCurrent}
            />
          </div>
        )
      ) : (
        <MonthYearPicker value={startDate} onChange={onStartDateChange} />
      )}

      {mode === "range" && (
        <div className="flex items-center gap-2 border-t pt-3">
          <Checkbox
            id="current-toggle"
            checked={isCurrent}
            onCheckedChange={(checked) => onCurrentToggle(checked === true)}
          />
          <Label htmlFor="current-toggle" className="cursor-pointer select-none text-sm">
            {presentLabel}
          </Label>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main DateRangeInput Component
// ============================================================================

export type DateRangeInputProps = {
  value?: string;
  onChange: (value: string) => void;
  mode?: "single" | "range";
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  presentLabel?: string;
};

const DateRangeInput = React.forwardRef<HTMLDivElement, DateRangeInputProps>(
  (
    { value = "", onChange, mode = "range", placeholder, className, disabled, presentLabel },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const isDesktop = useMediaQuery("(min-width: 640px)");
    const parsed = React.useMemo(() => parseDateValue(value), [value]);

    // Local state for editing
    const [startDate, setStartDate] = React.useState<ParsedDate | undefined>(parsed.startDate);
    const [endDate, setEndDate] = React.useState<ParsedDate | "current" | undefined>(
      parsed.endDate,
    );
    const [isCurrent, setIsCurrent] = React.useState(parsed.endDate === "current");
    const [legacyMode, setLegacyMode] = React.useState(parsed.isLegacy);
    const [legacyText, setLegacyText] = React.useState(parsed.legacyText ?? "");

    // Sync local state when value changes externally
    React.useEffect(() => {
      const p = parseDateValue(value);
      setStartDate(p.startDate);
      setEndDate(p.endDate);
      setIsCurrent(p.endDate === "current");
      setLegacyMode(p.isLegacy);
      setLegacyText(p.legacyText ?? "");
    }, [value]);

    // Update parent value when local state changes
    const updateValue = React.useCallback(
      (newStartDate: ParsedDate | undefined, newEndDate: ParsedDate | "current" | undefined) => {
        const newValue = buildValueString(newStartDate, newEndDate, mode);
        onChange(newValue);
      },
      [mode, onChange],
    );

    const handleStartDateChange = (date: ParsedDate) => {
      setStartDate(date);
      setLegacyMode(false);
      if (mode === "single") {
        updateValue(date, undefined);
      } else {
        updateValue(date, isCurrent ? "current" : endDate);
      }
    };

    const handleEndDateChange = (date: ParsedDate) => {
      setEndDate(date);
      setIsCurrent(false);
      updateValue(startDate, date);
    };

    const handleCurrentToggle = (checked: boolean) => {
      setIsCurrent(checked);
      if (checked) {
        setEndDate("current");
        updateValue(startDate, "current");
      } else {
        setEndDate(undefined);
        updateValue(startDate, undefined);
      }
    };

    const handleLegacyTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLegacyText(e.target.value);
      onChange(e.target.value);
    };

    const switchToPickerMode = () => {
      setLegacyMode(false);
      setLegacyText("");
      setStartDate(undefined);
      setEndDate(undefined);
      setIsCurrent(false);
    };

    // Display value
    const displayValue = React.useMemo(() => {
      if (legacyMode && legacyText) {
        return legacyText;
      }
      return formatDateForDisplay(value);
    }, [legacyMode, legacyText, value]);

    const defaultPlaceholder = mode === "single" ? "Select date" : "Select date range";

    const triggerButton = (
      <button
        type="button"
        disabled={disabled}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-lg border border-input bg-secondary px-3 py-1 text-sm shadow-sm transition-colors",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          !displayValue && "text-muted-foreground",
        )}
      >
        <span className="truncate">{displayValue || placeholder || defaultPlaceholder}</span>
        <Calendar className="ml-2 size-4 shrink-0 opacity-50" />
      </button>
    );

    const pickerContentProps: PickerContentProps = {
      mode,
      legacyMode,
      legacyText,
      startDate,
      endDate,
      isCurrent,
      onStartDateChange: handleStartDateChange,
      onEndDateChange: handleEndDateChange,
      onCurrentToggle: handleCurrentToggle,
      onLegacyTextChange: handleLegacyTextChange,
      onSwitchToPickerMode: switchToPickerMode,
      presentLabel,
    };

    // Desktop: Use Popover
    if (isDesktop) {
      return (
        <div ref={ref} className={cn("relative", className)}>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
            <PopoverContent className="w-auto p-4" align="start" sideOffset={4}>
              <PickerContent {...pickerContentProps} />
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    // Mobile: Use Drawer (bottom sheet)
    return (
      <div ref={ref} className={cn("relative", className)}>
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
          <DrawerContent>
            <DrawerHeader className="text-left">
              <DrawerTitle>{mode === "single" ? "Select Date" : "Select Date Range"}</DrawerTitle>
            </DrawerHeader>
            <div className="px-4">
              <PickerContent {...pickerContentProps} stacked />
            </div>
            <DrawerFooter>
              <DrawerClose asChild>
                <Button variant="outline" size="sm">
                  Done
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </div>
    );
  },
);

DateRangeInput.displayName = "DateRangeInput";

export { DateRangeInput };
